
import React, {  useState } from 'react';
import useCurrency from '../../../../components/CurrencyProvider';
import {
  Box,
  Typography,
  IconButton,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Collapse,

} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { IndentDetail } from '../Issue/Issue.helper';
import QtyUnitInput from '../common/QtyUnitInput';
import { useStockTransferStore, ReceiveSelectedItem } from '../../Store/useStockTransferstore';
import { ReactComponent as InfoCircleRed } from '../../../../assets/SVG/info-circle-red.svg';
import { ReactComponent as NoDataImage } from '../../../../assets/SVG/NoStockFound.svg';
import TableSkeleton from '../../../../components/common/TableSkeleton';
import IssueInformationPopup from '../Modals/IssueInformationPopup';
import { tableScrollSx } from '../../Common/inventory.helper';
import styles from '../StockTransfer.module.css';

// Information popup data shape
interface InfoData {
  indentNoDate: string;
  prNoDate: string;
  issueRemark: string;
}

export interface Column<T> {
  label: string;
  field: keyof T;
}
export type ReusableReceiveListTableProps<T extends { id: number | string; indent_details?: IndentDetail[] }> = {
  columns: Array<Column<T>>;
  data: T[]; 
  selectedKeys?: string[];
  onSelectionChange?: (selectedIds: Array<T['id']>) => void;
  page: number;
  rowsPerPage: number;
  totalCount: number;
  onPageChange: (newPage: number) => void;
  showCheckboxes?: boolean;
  className?: string;
  onRowClick?: (row: T) => void;
  isLoading?: boolean;
};

// ✅ Reusable InfoRow component for grid-based label-value pairs
const InfoRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <>
    <Typography variant="caption" sx={{ opacity: 0.8 }}>{label}</Typography>
    <Typography  variant="caption" sx={{ textAlign: 'left' }}>
      <Typography component="span" fontWeight={600} fontSize='14px' >{value}</Typography>
    </Typography>
  </>
);

// ✅ Helper component to render quantity with UOM label
const QtyWithUom = ({ qty, uomLabel }: { qty: number | string; uomLabel?: string }) => (
  <>
    {qty}
    {uomLabel && <Typography component="span" sx={{ fontSize: '11px' }}> ({uomLabel})</Typography>}
  </>
);

export default function   ReceiveProspectiveListTable<T extends { id: number | string; indent_details?: IndentDetail[];indentNoDate?: string; prNoDate?: string; issueRemark?: string;}>({
  columns,
  data,
  selectedKeys = [],
  onSelectionChange,
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  showCheckboxes = true,
  className,
  onRowClick,
  isLoading = false,
}: ReusableReceiveListTableProps<T>) {
  const { t: tStockTransfer } = useTranslation('stockTransfer_config');
  const { formatCurrency } = useCurrency();
  const getRowKey = (row: T, index: number) => (row.id !== undefined ? String(row.id) : `${index}`);
  const [openInfo, setOpenInfo] = useState(false);
  const [infoData, setInfoData] = useState<InfoData | null>(null);
  const isSelected = (rowKey: string) => selectedKeys.includes(rowKey);

  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [childCheckedByRow, setChildCheckedByRow] = useState<Record<string, boolean>>({});
  const [receivedQtyByRow, setReceivedQtyByRow] = useState<Record<string, number | string>>({});

  const { addSelectedReceiveItems, removeSelectedReceiveItemsByIssueDetailIds, selectedReceiveItems, setSelectedReceiveItems } = useStockTransferStore();

  const handleRowClick = (row: T, rowKey: string) => {
    setExpandedRow((prev) => (prev === rowKey ? null : rowKey));
    // eslint-disable-next-line
    onRowClick && onRowClick(row);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!onSelectionChange) return;
    if (event.target.checked) {
      const allIds = data.map((r) => r.id).filter((id) => id !== undefined) as Array<T['id']>;
      onSelectionChange(allIds);
    } else {
      onSelectionChange([]);
    }
  };

  const handleCheckboxClick = (row: T) => {
    if (!onSelectionChange) return;
    const id = row.id as T['id'];
    const selectedIds = data
      .map((r, i) => ({ id: r.id as T['id'], key: getRowKey(r, i) }))
      .filter((obj) => selectedKeys.includes(obj.key))
      .map((obj) => obj.id);
    const exists = selectedIds.includes(id);
    const next = exists ? selectedIds.filter((sid) => sid !== id) : [...selectedIds, id];
    onSelectionChange(next);

    // Auto-select/clear child checkboxes when parent toggles
    const rowKey = getRowKey(row, data.indexOf(row));
    const selecting = !exists;
    
    if (selecting) {
      // Select all child items and add to store
      const itemsToAdd: ReceiveSelectedItem[] = [];
      row.indent_details?.forEach((d: IndentDetail, idx: number) => {
        const baseChildKey = `${rowKey}_${idx}_0`;
        setChildCheckedByRow((prev) => ({ ...prev, [baseChildKey]: true }));
        
        // Add to store
        if (d.id) {
          const baseCost = Number(d.batch?.cost_price ?? d.base_cost_price ?? 0);
          const receivedQty = d.pending_qty ?? 0;
          const totalCost = (receivedQty * baseCost).toFixed(2);
          const sgstPercent = d.Taxes?.sgst?.percent ?? 0;
          const cgstPercent = d.Taxes?.cgst?.percent ?? 0;
          const sgstAmount = (Number(totalCost) * sgstPercent / 100).toFixed(2);
          const cgstAmount = (Number(totalCost) * cgstPercent / 100).toFixed(2);
          
          itemsToAdd.push({
            issue_detail_id: d.id,
            received_qty: receivedQty,
            base_mrp: String(d.batch?.mrp ?? d.base_mrp ?? '0'),
            base_cost_price: String(d.batch?.cost_price ?? d.base_cost_price ?? '0'),
            total_base_cost_price: totalCost,
            total_mrp_cost_price: String(d.total_mrp_cost_price ?? '0'),
            sgst_percent: sgstPercent,
            sgst_amount: sgstAmount,
            cgst_percent: cgstPercent,
            cgst_amount: cgstAmount,
          });
        }
      });
      if (itemsToAdd.length > 0) {
        addSelectedReceiveItems(itemsToAdd);
      }
    } else {
      // Deselect all child items and remove from store
      const idsToRemove: number[] = [];
      row.indent_details?.forEach((d: IndentDetail, idx: number) => {
        const baseChildKey = `${rowKey}_${idx}_0`;
        setChildCheckedByRow((prev) => ({ ...prev, [baseChildKey]: false }));
        if (d.id) {
          idsToRemove.push(d.id);
        }
      });
      if (idsToRemove.length > 0) {
        removeSelectedReceiveItemsByIssueDetailIds(idsToRemove);
      }
    }
  };

  return (
    <Box py={1} className={className} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <TableContainer sx={{ borderRadius: '12px', flex: 1, minHeight: 0, overflow: 'auto', ...tableScrollSx }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {showCheckboxes && (
                <TableCell padding="checkbox" sx={{ backgroundColor: '#F8F8F8', position: 'sticky', top: 0, zIndex: 3 }}>
                  <Checkbox
                    indeterminate={selectedKeys.length > 0 && selectedKeys.length < data.length}
                    checked={data.length > 0 && selectedKeys.length === data.length}
                    onChange={handleSelectAllClick}
                    sx={{
                      color: '#CFD1D4',
                      '&.Mui-checked': { color: '#4CAF50' },
                    }}
                  />
                </TableCell>
              )}
              
              {columns.map((col,) => (
                <TableCell key={String(col.field)} sx={{ fontWeight: 600, color: 'rgba(0, 0, 0, 0.7)', fontSize: '15px', backgroundColor: '#F8F8F8', position: 'sticky', top: 0, zIndex: 3 }}>
                  {col.label}
                </TableCell>
              ))}
              <TableCell sx={{ backgroundColor: '#F8F8F8', position: 'sticky', top: 0, zIndex: 3 }} />
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading ? (
              <TableSkeleton columnWidths={Array(columns.length + (showCheckboxes ? 2 : 1)).fill(150)} rows={rowsPerPage} />
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length + (showCheckboxes ? 2 : 1)} sx={{ borderBottom: 0 }}>
                  <Box className={styles.noDataImageStyle}>
                    <NoDataImage style={{ width: '600px', height: '400px' }} />
                    <Typography sx={{ mt: 1, color: '#666', fontWeight: 500 }}>
                      {tStockTransfer('No data available')}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              data.map((row, i) => {
                const rowKey = getRowKey(row, i);
                const isExpanded = expandedRow === rowKey;
                return (
                  <React.Fragment key={rowKey}>
                    <TableRow
                      selected={isExpanded}
                      onClick={() => handleRowClick(row, rowKey)}
                      sx={{
                        color: '#CFD1D4',
                        backgroundColor: isSelected(rowKey) ? '#FFF8F6' : undefined,
                        '&.Mui-checked': { color: '#4CAF50' },
                      }}
                    >
                      {showCheckboxes && (
                        <TableCell
                          padding="checkbox"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Checkbox
                            checked={isSelected(rowKey)}
                            onChange={() => handleCheckboxClick(row)}
                            sx={{ color: '#CFD1D4', '&.Mui-checked': { color: '#4CAF50' }, '&.Mui-disabled': { cursor: 'not-allowed' } }}
                          />
                        </TableCell>
                      )}
                      {columns.map((col) => (
                        <TableCell key={String(col.field)}>
                          {(row[col.field] as unknown as React.ReactNode) ?? ''}
                        </TableCell>
                      ))}

                      <TableCell>
                        <IconButton size="small" onClick={(e) => {
                          e.stopPropagation();
                          const payload: InfoData = {
                            indentNoDate: row.indentNoDate || '',
                            prNoDate: row.prNoDate || '',
                            issueRemark: row?.issueRemark || '',
                          };
                          setInfoData(payload);
                          setOpenInfo(true);
                        }}>
                          <InfoCircleRed />
                        </IconButton>
                      </TableCell>
                    </TableRow>

                    {/* Expanded row - accordion */}
                    <TableRow>
                      <TableCell
                        colSpan={columns.length + (showCheckboxes ? 1 : 0) + 1}
                        sx={{ padding: 0, borderBottom: 0 }}
                      >
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ background: '#FFF6F3', padding: 2 }}>
                            <Typography fontSize={'13px'} fontWeight={600} mb={1}>
                              {tStockTransfer('Indent Item List')}
                            </Typography>

                            <Box sx={{ overflowX: 'auto', width: '100%' }}>
                              <Table size="small" sx={{ tableLayout: 'fixed', width: '100%', minWidth: '1100px' }}>
                                <TableHead sx={{ backgroundColor: '#FFFFFF' }}> 
                                  <TableRow>
                                    <TableCell sx={{ width: '4%' }} />
                                    <TableCell sx={{ width: '14%' }}>{tStockTransfer('Item Code | Item Name')}</TableCell>
                                    <TableCell sx={{ width: '11%' }}>{tStockTransfer('Batch Code | Expiry Date')}</TableCell>
                                    <TableCell sx={{ width: '10%' }}>{tStockTransfer('Quantity | UOM')}</TableCell>
                                    <TableCell sx={{ width: '15%' }}>{tStockTransfer('Received Qty. | UOM')}</TableCell>
                                    <TableCell sx={{ width: '10%' }}>{tStockTransfer('SGST % | Amt.')}</TableCell>
                                    <TableCell sx={{ width: '10%' }}>{tStockTransfer('CGST % | Amt.')}</TableCell>
                                    <TableCell sx={{ width: '13%' }}>{tStockTransfer('Cost Amount')}</TableCell>
                                    <TableCell sx={{ width: '13%' }}>{tStockTransfer('MRP Amount')}</TableCell>
                                  </TableRow>
                                </TableHead>

                                <TableBody>
                                  {row.indent_details?.map((d: IndentDetail, idx: number) => {
                                    const key = `${idx}_0`;
                                    const batchCode = d.batch?.batch_number ?? '-';
                                    const expiryDate = d.batch?.expiry_date ?? '-';
                                    const indentQty = d.indent_qty ?? 0;
                                    const pendingQty = d.pending_qty ?? 0;
                                    const issueQty = d.issue_qty ?? 0;
                                    const baseCost = Number(d.batch?.cost_price ?? d.base_cost_price ?? 0);
                                    const baseMrp = Number(d.batch?.mrp ?? d.base_mrp ?? 0);
                                    const Unit = d.uom_label ?? d.uom;
                                
                                    // Get tax percentages from API (these remain constant)
                                    const sgstPercent = d.Taxes?.sgst?.percent ?? 0;
                                    const cgstPercent = d.Taxes?.cgst?.percent ?? 0;
                                
                                    // Calculate dynamic total amounts based on received quantity
                                    const errorKey = `${rowKey}_${key}`;
                                    const currentReceivedQty = receivedQtyByRow[errorKey] !== undefined 
                                      ? Number(receivedQtyByRow[errorKey]) 
                                      : pendingQty;
                                    const totalCostAmount = (currentReceivedQty * baseCost).toFixed(2);
                                    const totalMrpAmount = (currentReceivedQty * baseMrp).toFixed(2);
                                  
                                    // Calculate dynamic tax amounts based on total cost amount
                                    const dynamicSgstAmount = (Number(totalCostAmount) * sgstPercent / 100).toFixed(2);
                                    const dynamicCgstAmount = (Number(totalCostAmount) * cgstPercent / 100).toFixed(2);
                                
                                    return (
                                      <TableRow key={`row_${idx}_0`}>
                                        <TableCell>
                                          <Box display="flex" alignItems="center" gap={1} sx={{ cursor: 'pointer' }}>
                                            {showCheckboxes && (
                                              <Checkbox
                                                size="small"
                                                sx={{ color: '#CFD1D4', '&.Mui-checked': { color: '#4CAF50' } }}
                                                checked={!!childCheckedByRow[`${rowKey}_${idx}_0`]}
                                                onChange={(e) => {
                                                  const ck = `${rowKey}_${idx}_0`;
                                                  const nextVal = e.target.checked;
                                              
                                                  // Update child checkbox state
                                                  setChildCheckedByRow((prev) => {
                                                    const updated = { ...prev, [ck]: nextVal };
                                                
                                                    // Check if any child will be selected after this change
                                                    const willHaveAnyChildSelected = row.indent_details?.some((detail: IndentDetail, detailIdx: number) => {
                                                      const childKey = `${rowKey}_${detailIdx}_0`;
                                                      return !!updated[childKey];
                                                    }) ?? false;
                                                
                                                    // Auto-unselect parent when no children are selected
                                                    if (!nextVal && !willHaveAnyChildSelected && isSelected(rowKey) && onSelectionChange) {
                                                      const id = row.id as T['id'];
                                                      // Compute current selectedIds from data (includes current parent)
                                                      // This is necessary because we need the full list to filter out the parent
                                                      const selectedIds = data
                                                        .map((r, i) => ({ id: r.id as T['id'], key: getRowKey(r, i) }))
                                                        .filter((obj) => selectedKeys.includes(obj.key))
                                                        .map((obj) => obj.id);
                                                      onSelectionChange(selectedIds.filter(sid => sid !== id));
                                                    }
                                                
                                                    return updated;
                                                  });
                                              
                                                  // Auto-select parent when any child is selected (before store update to ensure consistency)
                                                  if (nextVal && !isSelected(rowKey) && onSelectionChange) {
                                                    const id = row.id as T['id'];
                                                    // Compute current selectedIds from data (maintains consistency with unselect logic)
                                                    // While we could directly use selectedKeys here, using this pattern ensures
                                                    // consistency across both select/unselect operations for maintainability
                                                    const selectedIds = data
                                                      .map((r, i) => ({ id: r.id as T['id'], key: getRowKey(r, i) }))
                                                      .filter((obj) => selectedKeys.includes(obj.key))
                                                      .map((obj) => obj.id);
                                                    onSelectionChange([...selectedIds, id]);
                                                  }
                                              
                                                  // Update Zustand store after parent selection to maintain consistency
                                                  if (nextVal && d.id) {
                                                    // Add item to store
                                                    const receivedQty = receivedQtyByRow[`${rowKey}_${key}`] !== undefined ? Number(receivedQtyByRow[`${rowKey}_${key}`]) : (d.pending_qty ?? 0);
                                                    const totalCost = (receivedQty * baseCost).toFixed(2);
                                                    const sgstAmount = (Number(totalCost) * sgstPercent / 100).toFixed(2);
                                                    const cgstAmount = (Number(totalCost) * cgstPercent / 100).toFixed(2);
                                                  
                                                    addSelectedReceiveItems([{
                                                      issue_detail_id: d.id,
                                                      received_qty: receivedQty,
                                                      base_mrp: String(d.batch?.mrp ?? d.base_mrp ?? '0'),
                                                      base_cost_price: String(d.batch?.cost_price ?? d.base_cost_price ?? '0'),
                                                      total_base_cost_price: totalCost,
                                                      total_mrp_cost_price: String(d.total_mrp_cost_price ?? '0'),
                                                      sgst_percent: sgstPercent,
                                                      sgst_amount: sgstAmount,
                                                      cgst_percent: cgstPercent,
                                                      cgst_amount: cgstAmount,
                                                    }]);
                                                  } else if (!nextVal && d.id) {
                                                    // Remove item from store
                                                    removeSelectedReceiveItemsByIssueDetailIds([d.id]);
                                                  }
                                                }}
                                              />
                                            )}
                                          </Box>
                                        </TableCell>
                                        <TableCell>{`${d.item?.code ?? '-'} | ${d.item?.name_label ?? d.item?.name ?? '-'}`}</TableCell>
                                        <TableCell>
                                          <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                              {tStockTransfer('Batch Code')}: {batchCode}
                                            </Typography>
                                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.8 }}>
                                              {tStockTransfer('Expiry Date')}: {expiryDate}
                                            </Typography>
                                          </Box>
                                        </TableCell>
                                        <TableCell>
                                          {(() => {
                                            const errorKey = `${rowKey}_${key}`;
                                            const receivedQty = receivedQtyByRow[errorKey] !== undefined 
                                              ? Number(receivedQtyByRow[errorKey]) 
                                              : pendingQty;
                                            const dynamicPendingQty = Math.max(0, pendingQty - receivedQty);
                                        
                                            return (
                                              <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1, rowGap: 0.5, alignItems: 'center' }}>
                                                <InfoRow label={`${tStockTransfer('Indent')} :`} value={<QtyWithUom qty={indentQty} uomLabel={d.indent_uom_label} />} />
                                                <InfoRow label={`${tStockTransfer('Issued')} :`} value={<QtyWithUom qty={issueQty} uomLabel={d.uom_label} />} />
                                                <InfoRow label={`${tStockTransfer('Pending')} :`} value={<QtyWithUom qty={dynamicPendingQty} uomLabel={d.uom_label} />} />
                                              </Box>
                                            );
                                          })()}
                                        </TableCell>
                                        <TableCell>
                                          {(() => {
                                            const errorKey = `${rowKey}_${key}`;
                                            const enteredQty = Number(receivedQtyByRow[errorKey] ?? pendingQty);
                                            const hasError = enteredQty > issueQty;
                                            return (
                                              <Box>
                                                <QtyUnitInput 
                                                  qty={receivedQtyByRow[errorKey] !== undefined ? Number(receivedQtyByRow[errorKey]) : pendingQty}
                                                  onQtyChange={(qty) => {
                                                    setReceivedQtyByRow((prev) => ({ ...prev, [errorKey]: qty }));
                                                
                                                    // Update Zustand store if item is checked
                                                    const childKey = `${rowKey}_${idx}_0`;
                                                    if (childCheckedByRow[childKey] && d.id) {
                                                      const newQty = Number(qty);
                                                      const newTotalCost = (newQty * baseCost).toFixed(2);
                                                      const newTotalMrp = (newQty * baseMrp).toFixed(2);
                                                      const newSgstAmount = (Number(newTotalCost) * sgstPercent / 100).toFixed(2);
                                                      const newCgstAmount = (Number(newTotalCost) * cgstPercent / 100).toFixed(2);
                                                    
                                                      const updatedItem: ReceiveSelectedItem = {
                                                        issue_detail_id: d.id,
                                                        received_qty: newQty,
                                                        base_mrp: String(d.batch?.mrp ?? d.base_mrp ?? '0'),
                                                        base_cost_price: String(d.batch?.cost_price ?? d.base_cost_price ?? '0'),
                                                        total_base_cost_price: newTotalCost,
                                                        total_mrp_cost_price: newTotalMrp,
                                                        sgst_percent: sgstPercent,
                                                        sgst_amount: newSgstAmount,
                                                        cgst_percent: cgstPercent,
                                                        cgst_amount: newCgstAmount,
                                                      };
                                                  
                                                      // Find and update the item in store
                                                      const existingIndex = selectedReceiveItems.findIndex(
                                                        item => item.issue_detail_id === d.id
                                                      );
                                                  
                                                      if (existingIndex !== -1) {
                                                        const updatedItems = [...selectedReceiveItems];
                                                        updatedItems[existingIndex] = updatedItem;
                                                        setSelectedReceiveItems(updatedItems);
                                                      }
                                                    }
                                                  }}
                                                  onQtyUnitChange={() => {}}
                                                  qtyUnit={Unit}
                                                  hasError={hasError}
                                                  isDisableDropdown={true}
                                                />
                                                {hasError && (
                                                  <Typography variant="caption" sx={{ color: '#d32f2f', fontSize: '11px', mt: 0.5 }}>
                                                    {tStockTransfer('Received quantity must not exceed the issued quantity')}
                                                  </Typography>
                                                )}
                                              </Box>
                                            );
                                          })()}
                                        </TableCell>
                                        <TableCell>
                                          <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1, rowGap: 0.5, alignItems: 'center' }}>
                                            <InfoRow label={`${tStockTransfer('Percent')} :`} value={`${sgstPercent}%`} />
                                            <InfoRow label={`${tStockTransfer('Amount')} :`} value={formatCurrency(Number(dynamicSgstAmount))} />
                                          </Box>
                                        </TableCell>
                                        <TableCell>
                                          <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1, rowGap: 0.5, alignItems: 'center' }}>
                                            <InfoRow label={`${tStockTransfer('Percent')} :`} value={`${cgstPercent}%`} />
                                            <InfoRow label={`${tStockTransfer('Amount')} :`} value={formatCurrency(Number(dynamicCgstAmount))} />
                                          </Box>
                                        </TableCell>
                                  
                                        <TableCell>
                                          <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1, rowGap: 0.5, alignItems: 'center' }}>
                                            <InfoRow label={`${tStockTransfer('Base Cost')} :`} value={formatCurrency(baseCost)} />
                                            <InfoRow label={`${tStockTransfer('Total Cost Amount')} :`} value={formatCurrency(Number(totalCostAmount))} />
                                          </Box>
                                        </TableCell>
                                        <TableCell>
                                          <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1, rowGap: 0.5, alignItems: 'center' }}>
                                            <InfoRow label={`${tStockTransfer('Base MRP')} :`} value={formatCurrency(baseMrp)} />
                                            <InfoRow label={`${tStockTransfer('Total MRP Amount')} :`} value={formatCurrency(Number(totalMrpAmount))} />
                                          </Box>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} px={1} sx={{ flexShrink: 0, backgroundColor: '#fff', pt: 2 }}>
        <Typography fontSize={13} color="text.secondary">
          {tStockTransfer('Showing {{from}} to {{to}} of {{total}} entries', {
            from: totalCount === 0 ? 0 : page * rowsPerPage + 1,
            to: Math.min((page + 1) * rowsPerPage, totalCount),
            total: totalCount
          })}
        </Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <Pagination
            count={Math.ceil(totalCount / rowsPerPage) || 1}
            page={page + 1}
            onChange={(_, v) => onPageChange(v - 1)}
            shape="rounded"
          />
        </Box>
      </Box>
      <IssueInformationPopup
        open={openInfo}
        onClose={() => setOpenInfo(false)}
        data={infoData || null}
        IsFromPropsReceive={true}
      />
    </Box>
  );
}
